import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import VerifiedBadge from "@/components/VerifiedBadge";
import { Trophy, UserCircle2 } from "lucide-react";

const WINDOWS = [
  { key: 7, label: "7 days" },
  { key: 30, label: "30 days" },
  { key: 90, label: "90 days" },
];

export default function Leaderboard() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/leaderboard", { params: { window_days: days, limit: 25 } })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="mx-auto max-w-[1200px] px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CCFF00]">
            // Leaderboard · Public
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3 flex items-center gap-3">
            <Trophy className="w-9 h-9 text-[#CCFF00]" /> Top creators
          </h1>
          <p className="mt-3 text-white/60 max-w-lg">
            Ranked by paid sales in the selected window. Sell more, climb higher.
          </p>
        </div>
        <div className="flex gap-1 border border-white/15 p-0.5" data-testid="leaderboard-window-toggle">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              data-testid={`leaderboard-window-${w.key}`}
              onClick={() => setDays(w.key)}
              className={`font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-1.5 ${days === w.key ? "bg-white text-black" : "text-white/70 hover:text-white"}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="font-mono text-xs text-white/50">Loading rankings…</div>
      ) : !data || data.creators.length === 0 ? (
        <div data-testid="leaderboard-empty" className="border border-dashed border-white/15 py-24 text-center">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40">// SILENCE</div>
          <p className="mt-4 text-white/70">No paid sales in this window yet. Be the first to top the chart.</p>
          <Link to="/" className="inline-block mt-6 bg-[#FF3366] text-white font-mono text-xs uppercase tracking-[0.2em] px-4 py-2">
            Browse the store →
          </Link>
        </div>
      ) : (
        <div className="border border-white/10 bg-[#0A0A0A]">
          {data.creators.map((c, i) => (
            <Link
              key={c.user_id}
              to={`/creator/${c.user_id}`}
              data-testid={`leaderboard-row-${c.user_id}`}
              className="flex items-center gap-4 p-4 border-b border-white/10 last:border-b-0 hover:bg-white/5"
            >
              <div className={`font-mono font-bold text-2xl w-10 text-center ${i === 0 ? "text-[#CCFF00]" : i < 3 ? "text-white" : "text-white/40"}`}>
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="shrink-0">
                {c.picture ? (
                  <img src={c.picture} alt="" className="w-12 h-12 object-cover border border-white/15" />
                ) : (
                  <div className="w-12 h-12 border border-white/15 bg-[#141414] flex items-center justify-center">
                    <UserCircle2 className="w-6 h-6 text-white/40" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-lg truncate">{c.name}</span>
                  {c.is_verified && <VerifiedBadge testId={`leaderboard-verified-${c.user_id}`} />}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">
                  {c.drops} drops · {c.sales} sold · ${c.revenue.toFixed(2)}
                </div>
              </div>
              <div className="hidden sm:block text-right">
                <div className="font-mono font-bold text-2xl text-[#CCFF00]">{c.sales}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">sales</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
